import React, { FunctionComponent, useEffect, useState } from "react"
import Modal from "react-modal"
import Provider from "../../utils/connected-apollo-provider"
import { AxiosResponse } from "axios"
import { useMutation } from "@apollo/client"
import { useForm } from "react-hook-form"
import { Button, Icon } from "@kuali/pure-ui"

import { UPDATE_PLAN_STATUS } from "./queries"
import { loadPlanStatus } from "./load-status"
import "./modal.scss"

interface Props {
  buttonText?: string
  iconName?: string
  shouldOpenModalByDefault?: boolean
  shouldShowTriggerButton?: boolean
  onModalOpen?: () => void
  onModalClose?: () => void
}

interface State {
  currentStatus?: string
  otherStatuses?: {}
  newStatusItem?: {}
}

type FormData = {
  planStatus: string
  planComments: string
}

const positioningStyles = {
  content: {
    top: "0",
    left: "0",
    right: "0",
    bottom: "0",
    width: "100%",
    background: "none",
    border: "none"
  },
  overlay: {
    background: "rgba(255, 255, 255, 0.95)"
  }
}

Modal.setAppElement("body")

const ModalUpdateStatus: FunctionComponent<Props> = ({
  buttonText = "",
  shouldOpenModalByDefault = false,
  shouldShowTriggerButton = true,
  onModalOpen,
  onModalClose
}) => {
  // TODO - pass planId from route when able to do so
  const planId = Number(document.body.dataset.planId)
  const { register, errors, handleSubmit, formState } = useForm<FormData>({
    mode: "onChange"
  })
  const { isDirty } = formState
  const [modalIsOpen, setIsOpen] = React.useState(shouldOpenModalByDefault)

  const [planData, setPlanData] = useState<State>({
    currentStatus: "",
    otherStatuses: {}
  })

  // mutation to update plan status
  const [
    createStatusUpdate,
    { loading: isStatusUpdating, error: updateError }
  ] = useMutation<{
    createStatusUpdate
  }>(UPDATE_PLAN_STATUS, {
    errorPolicy: "all",
    onCompleted({ createStatusUpdate }) {
      if (createStatusUpdate) {
        setIsOpen(false)

        if (window) {
          window.location.reload()
        }
      }
    },
    onError(error) {
      // console.log(error)
    }
  })

  let isNoteRequired = false

  // form `isDirty` state represents any change to form, so form is considered invalid
  // in its default state until either notes or status changes
  if (!isDirty) {
    isNoteRequired = true
  }

  useEffect(() => {
    // TODO - convert endpoint that fetches statuses to use GraphQL
    const fetchData = async (): Promise<AxiosResponse | void> => {
      // temporary API path for testing
      const currentPlanPath = window ? window.location.href + ".json" : ""
      const result = await loadPlanStatus(currentPlanPath)

      if (result) {
        setPlanData({
          currentStatus: result.currentStatus,
          otherStatuses: result.otherStatuses
        })
      }
    }

    fetchData()
  }, [])

  onModalOpen = (): void => {
    setIsOpen(true)
  }

  onModalClose = (): void => {
    setIsOpen(false)
  }

  const onSubmit = handleSubmit(({ planStatus, planComments }) => {
    // Find the key of the current status by value to send along to BE,
    const currentStatusKey = Object.keys(planData.otherStatuses).find(
      (key) => planData.otherStatuses[key] === planStatus
    )

    const postData = (planId, planStatus, planComments) => {
      createStatusUpdate({
        variables: {
          planId: planId,
          comment: planComments,
          status: currentStatusKey
        }
      })
    }
    postData(planId, currentStatusKey, planComments)
  })

  const getStatusOptions = (statuses: {}): JSX.Element | Array<void> => {
    const statusEls = []
    for (const key in statuses) {
      statusEls.push(
        <option key={key} value={statuses[key]}>
          {statuses[key]}
        </option>
      )
    }

    return statusEls
  }

  const getCSSFriendlyColorName = (generatedString: string) => {
    return generatedString
      .toLowerCase()
      .trim()
      .replace(/\s/g, "-")
  }

  return (
    <div>
      {shouldShowTriggerButton && (
        <Button size="large" kind="solid" onClick={onModalOpen}>
          {buttonText}
        </Button>
      )}
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={onModalClose}
        style={positioningStyles}
        shouldCloseOnOverlayClick={false}
        contentLabel="Update Status Modal"
      >
        <div className="modal-content">
          <div className="--text-right">
            <Button
              size="small"
              kind="transparent"
              icon={<Icon name="close" />}
              onClick={onModalClose}
            >
              <span className="visually-hidden">Close</span>
            </Button>
          </div>
          <main className="centered">
            <header>
              <h2>Update the Plan Status</h2>
            </header>
            <section className="stack">
              <p>Manage your plan by keeping the plan status up to date</p>
              {planData && planData.currentStatus && (
                <React.Fragment>
                  <h3>Current Status</h3>
                  <p className="status-area">
                    <span
                      className={`visual-indicator visual-indicator--${getCSSFriendlyColorName(
                        planData.currentStatus
                      )}`}
                    ></span>
                    {planData.currentStatus}
                  </p>
                </React.Fragment>
              )}
              <div>
                <form onSubmit={onSubmit} data-testid="status-form">
                  <div className="stack">
                    <label htmlFor="status">Update Status</label>
                    <select
                      name="planStatus"
                      id="status"
                      ref={register({ required: true })}
                    >
                      {planData &&
                        planData.otherStatuses &&
                        getStatusOptions(planData.otherStatuses)}
                    </select>
                    {errors.planStatus && (
                      <span className="error-message">This field is required</span>
                    )}
                    <label htmlFor="status-comments">Comments</label>
                    <textarea
                      name="planComments"
                      id="status-comments"
                      ref={register({ required: false })}
                    ></textarea>
                    <div className="cluster">
                      <div className="--flex-end" data-testid="status-submit">
                        <Button
                          size="large"
                          kind="transparent"
                          onClick={onModalClose}
                        >
                          No update
                        </Button>
                        <Button
                          size="large"
                          kind="solid"
                          type="submit"
                          disabled={isNoteRequired}
                        >
                          Update Status
                        </Button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </section>
          </main>
        </div>
      </Modal>
    </div>
  )
}

const UpdateStatusWithApollo = (props) => {
  // wrap Plan Status in Provider HOC to connect to Apollo -
  // would otherwise be passed in via routing on full page, will be done on full move to React
  return (
    <Provider>
      <ModalUpdateStatus {...props} />
    </Provider>
  )
}

export default UpdateStatusWithApollo
